import { MADE_IN_UKRAINE_CHARACTERISTIC_GROUP_ID } from '@constants';

export const DEFAULT_COEFFICIENT = 4.8;

export const MAX_PHOTO_POINTS = 4;
export const MAX_TITLE_POINTS = 3;
export const MAX_DESCRIPTION_POINTS = 3;
export const MAX_CATEGORY_POINTS = 1;
export const MAX_COLORS_POINTS = 1;
export const MAX_SIZES_POINTS = 1;
export const MAX_CHARACTERISTIC_POINTS = 1;
export const MAX_CONDITION_POINTS = 1;
export const MAX_PRICE_POINTS = 1;
export const MAX_KEYWORDS_POINTS = 1;

interface CharacteristicsGroup {
  id: number,
}

export interface ProductData {
  photos?: object[],
  titleUk?: string,
  titleRu?: string,
  descriptionUk?: string,
  descriptionRu?: string,
  catalogSlug?: string,
  price?: string | number | null,
  condition?: object | null,
  colors?: (object | null)[] | null,
  sizes?: object[],
  characteristics?: CharacteristicsGroup[],
  keywords?: string[] | null,
}

const calculatePhotoPoints = ({
  photos,
} : {
  photos?: ProductData['photos'],
}) => {
  if ((photos?.length || 0) >= 3) {
    return MAX_PHOTO_POINTS;
  } else if ((photos?.length || 0) === 2) {
    return 3;
  } else if ((photos?.length || 0) === 1) {
    return 2;
  }

  return 0;
};

const calculateTitlePoints = ({
  titleUk,
  titleRu,
} : {
  titleUk?: ProductData['titleUk'],
  titleRu?: ProductData['titleRu'],
}) => {
  if (Math.max(titleUk?.length || 0, titleRu?.length || 0) > 29) {
    return MAX_TITLE_POINTS;
  } else if (titleUk?.length || titleRu?.length) {
    return 2;
  }

  return 0;
};

const calculateDescriptionPoints = ({
  descriptionUk,
  descriptionRu,
} : {
  descriptionUk?: ProductData['descriptionUk'],
  descriptionRu?: ProductData['descriptionRu'],
}) => {
  if (Math.max(descriptionUk?.length || 0, descriptionRu?.length || 0) > 99) {
    return MAX_DESCRIPTION_POINTS;
  } else if (descriptionUk?.length || descriptionRu?.length) {
    return 2;
  }

  return 0;
};

const calculateCharacteristicPoints = ({
  characteristics,
} : {
  characteristics?: ProductData['characteristics'],
}) => (
  characteristics?.filter(
    (group) => group.id !== MADE_IN_UKRAINE_CHARACTERISTIC_GROUP_ID,
  ).length || 0
);

export const calculatePoints = ({
  photos,
  titleUk,
  titleRu,
  descriptionUk,
  descriptionRu,
  catalogSlug,
  price,
  colors,
  sizes,
  characteristics,
  condition,
  keywords,
} : ProductData) => {
  const photoPoints = calculatePhotoPoints({ photos });
  const titlePoints = calculateTitlePoints({ titleUk, titleRu });
  const descriptionPoints = calculateDescriptionPoints({ descriptionUk, descriptionRu });
  const catalogPoints = catalogSlug ? MAX_CATEGORY_POINTS : 0;
  const pricePoints = price ? MAX_PRICE_POINTS : 0;
  const colorsPoints = colors?.length ? MAX_COLORS_POINTS : 0;
  const sizesPoints = sizes?.length ? MAX_SIZES_POINTS : 0;
  const characteristicPoints = calculateCharacteristicPoints({ characteristics });
  const conditionPoints = condition ? MAX_CONDITION_POINTS : 0;
  const keywordPoints = keywords?.length ? MAX_KEYWORDS_POINTS : 0;

  return (
    photoPoints
    + titlePoints
    + descriptionPoints
    + catalogPoints
    + pricePoints
    + conditionPoints
    + colorsPoints
    + sizesPoints
    + characteristicPoints
    + keywordPoints
  );
};

const getMaxPoints = ({
  colors,
  sizes,
  characteristics,
} : {
  colors: ProductData['colors'],
  sizes: ProductData['sizes'],
  characteristics: ProductData['characteristics'],
}) => {
  const maxCharacteristicPoints = characteristics?.length || 0;
  const maxSizePoints = sizes?.length ? MAX_SIZES_POINTS : 0;
  const maxColorPoints = colors?.length ? MAX_COLORS_POINTS : 0;

  return (
    MAX_PHOTO_POINTS
    + MAX_TITLE_POINTS
    + MAX_DESCRIPTION_POINTS
    + MAX_CATEGORY_POINTS
    + MAX_CONDITION_POINTS
    + MAX_PRICE_POINTS
    + MAX_KEYWORDS_POINTS
    + maxCharacteristicPoints
    + maxSizePoints
    + maxColorPoints
  );
};

export const getCoefficient = ({
  catalogSlug,
  colors,
  sizes,
  characteristics,
} : {
  catalogSlug?: string,
  colors: ProductData['colors'],
  sizes: ProductData['sizes'],
  characteristics: ProductData['characteristics'],
}) => {
  const maxPoints = getMaxPoints({
    colors,
    sizes,
    characteristics,
  });

  if (!catalogSlug) {
    return DEFAULT_COEFFICIENT;
  }

  return 100 / maxPoints;
};
